import type {Module} from "vuex";
import type {PermissionRestriction} from "@/global/types/permission";
import type {RootState} from "@/store/types";
import {platform} from "@/api/platform";
import {PERMISSIONS} from "@/constants";
import permissionsHelper from '@helpers_permissions';

export type PermissionRestrictions = Record<string, PermissionRestriction>

export interface PermissionsState {
    permissions: string[] | null
    restrictions: PermissionRestrictions | null
}

const permissions: Module<PermissionsState, RootState> = {
    namespaced: true,
    state: {
        permissions: null,
        restrictions: null
    },
    getters: {
        permissions: (state): string[] | null => state.permissions,
        restrictions: (state): PermissionRestrictions | null => state.restrictions,
        hasPermission: state => (permission: string): boolean => {

            return null !== state.permissions && -1 !== state.permissions.indexOf(permission);
        },
        hasRestriction: state => (permission: string): boolean | null => {

            return state.restrictions
                && undefined !== state.restrictions[permission]
                && true === permissionsHelper.isRestricted(state.restrictions[permission])
        },
        getRestriction: state => (permission: string): PermissionRestriction => {

            if (state.restrictions && state.restrictions[permission]) {

                return state.restrictions[permission];
            }

            return {};
        },
        // Has the permission and it is not restricted
        hasPermissionAccess: state => (permission: string): boolean => null !== state.permissions
            && -1 !== state.permissions.indexOf(permission)
            && !(
                state.restrictions
                && undefined !== state.restrictions[permission]
                && true === permissionsHelper.isRestricted(state.restrictions[permission])
            )
    },
    mutations: {
        setPermissions: (state, permissions: string[] | null) => state.permissions = permissions,
        setRestrictions: (state, restrictions: PermissionRestrictions | null) => state.restrictions = restrictions,
    },
    actions: {
        setPermissions: (context, permissions: string[] | null) => context.commit("setPermissions", permissions),
        setRestrictions: (context, restrictions: PermissionRestrictions | null) => context.commit("setRestrictions", restrictions),
        refresh(context) {

            return platform
                .get<{results: {permissions: string[], validationMap: PermissionRestriction}}>(`/api/v2/consumers/${this.getters["user/id"]}`, {params: {get: "permissions,validationMap"}})
                .then(response => {

                    context.commit("setRestrictions", {[PERMISSIONS.EMPLOYEE]: response.data.results.validationMap})
                    context.commit(
                        "setPermissions",
                        // An impersonator only gets the employee permission (if the user has it)
                        this.getters["user/impersonatorToken"]
                            ? (-1 !== response.data.results.permissions.indexOf(PERMISSIONS.EMPLOYEE) ? [PERMISSIONS.EMPLOYEE] : [])
                            : response.data.results.permissions
                    )
                })
        },
        clear: (context) => {

            context.commit("setPermissions", null);
            context.commit("setRestrictions", null);
        }
    }
}

export default permissions
